import { useRef, useState } from 'react';
import { Camera, ImagePlus, Loader2 } from 'lucide-react';

import { MAX_PHOTOS_PER_ENTRY, MAX_HUNT_PHOTOS } from '@/services/photo-service';
import { InlineErrorBanner } from '@/components/common/InlineErrorBanner';
import { useAuthStore } from '@/stores/auth-store';
import type { PhotoRecord } from '@/types/photo';

export interface PhotoPickerProps {
  /** ID of the parent log entry (legacy — use entityType + entityId for new entities) */
  logEntryId?: string;
  /** Entity type for multi-entity support. Defaults to 'logEntry' when logEntryId is provided. */
  entityType?: 'logEntry' | 'hunt' | 'harvest' | 'sit';
  /** Entity ID when using entityType. Takes precedence over logEntryId. */
  entityId?: string;
  /** Hunt ID — required when entityType === 'sit' so photos are dual-tagged */
  huntId?: string;
  existingCount: number;
  onPhotosAdded: (photos: PhotoRecord[]) => void;
  onAddFiles?: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * PhotoPicker provides two ways to attach photos:
 * 1. Camera capture — opens rear camera on mobile
 * 2. File picker — opens file browser (gallery on mobile, file system on desktop)
 *
 * When logEntryId is provided, photos are saved immediately via photo-service.
 * When onAddFiles is provided instead, files are handed back for deferred saving (create mode).
 */
export function PhotoPicker({
  logEntryId,
  entityType,
  entityId,
  huntId,
  existingCount,
  onPhotosAdded,
  onAddFiles,
  disabled = false,
}: PhotoPickerProps) {
  // Resolve effective entity type and ID for immediate-save mode
  const effectiveEntityType = entityType ?? 'logEntry';
  const effectiveEntityId = entityId ?? logEntryId ?? '';
  const userId = useAuthStore((s) => s.user?.id);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Hunt trips allow more photos than log entries (J-4)
  const maxPhotos = effectiveEntityType === 'hunt' ? MAX_HUNT_PHOTOS : MAX_PHOTOS_PER_ENTRY;
  const remaining = maxPhotos - existingCount;
  const isAtLimit = remaining <= 0;
  // Also disabled when user is not authenticated (belt-and-suspenders — AuthGuard covers the route)
  const isDisabled = disabled || isAtLimit || isProcessing || !userId;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files).slice(0, remaining);
    if (fileArray.length === 0) return;

    if (!userId) {
      console.error('[PhotoPicker] Cannot add photos: user not authenticated');
      setUploadError('Cannot add photos: user not authenticated');
      return;
    }

    if (onAddFiles) {
      // Deferred mode — hand back files to parent (create mode)
      onAddFiles(fileArray);
      return;
    }

    // Immediate mode — save now (edit mode)
    setIsProcessing(true);
    setUploadError(null);
    try {
      const photoService = await import('@/services/photo-service');
      let records: PhotoRecord[];
      if (effectiveEntityType === 'hunt') {
        records = await photoService.addPhotosToHunt(effectiveEntityId, userId, fileArray);
      } else if (effectiveEntityType === 'harvest') {
        records = await photoService.addPhotosToHarvest(effectiveEntityId, userId, fileArray);
      } else if (effectiveEntityType === 'sit') {
        // sitId = effectiveEntityId; huntId required for dual-tagging
        const resolvedHuntId = huntId ?? '';
        records = await photoService.addPhotosToSit(
          effectiveEntityId,
          resolvedHuntId,
          userId,
          fileArray,
        );
      } else {
        records = await photoService.addPhotosToEntry(effectiveEntityId, userId, fileArray);
      }
      onPhotosAdded(records);
    } catch (err) {
      console.error('[PhotoPicker] Failed to add photos:', err);
      const message = err instanceof Error ? err.message : 'Failed to save photo. Please try again.';
      setUploadError(message);
    } finally {
      setIsProcessing(false);
    }
  }

  const buttonBase =
    'flex items-center gap-2 px-3 py-2 rounded-md text-body-sm font-medium border transition-colors';
  const buttonEnabled =
    'bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-subtle)]';
  const buttonDisabled = 'opacity-40 cursor-not-allowed bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-muted)]';

  return (
    <div className="ph-no-capture flex flex-col gap-2" data-testid="photo-picker">
      <div className="flex items-center justify-between">
        <span className="text-body-xs text-[var(--color-text-muted)]">
          {isAtLimit
            ? `Maximum photos reached (${maxPhotos})`
            : `${remaining} of ${maxPhotos} remaining`}
        </span>
        {isProcessing && (
          <span className="flex items-center gap-1 text-body-xs text-[var(--color-text-muted)]">
            <Loader2 size={12} className="animate-spin" />
            Processing…
          </span>
        )}
      </div>

      <div className="flex gap-2">
        {/* Camera capture — single shot, opens rear camera on mobile */}
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          disabled={isDisabled}
          className={`${buttonBase} ${isDisabled ? buttonDisabled : buttonEnabled}`}
          aria-label="Take photo with camera"
          data-testid="camera-button"
        >
          <Camera size={16} />
          Camera
        </button>

        {/* File picker — multiple files from gallery/filesystem */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isDisabled}
          className={`${buttonBase} ${isDisabled ? buttonDisabled : buttonEnabled}`}
          aria-label="Choose photos from files"
          data-testid="file-button"
        >
          <ImagePlus size={16} />
          Choose Files
        </button>
      </div>

      {/* Upload error — surfaces failures from immediate-save mode */}
      {uploadError !== null && (
        <InlineErrorBanner message={uploadError} data-testid="photo-picker-error" />
      )}

      {/* Off-screen inputs — opacity-0 instead of display:none for reliable .click() on mobile */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="absolute opacity-0 pointer-events-none w-px h-px"
        onChange={(e) => void handleFiles(e.target.files)}
        data-testid="camera-input"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="absolute opacity-0 pointer-events-none w-px h-px"
        onChange={(e) => void handleFiles(e.target.files)}
        data-testid="file-input"
      />
    </div>
  );
}
